import { useState, useEffect, useCallback } from 'react';
import Navbar from '../components/Navbar';
import { getRankedFilms } from '../lib/supabase';

const PRESETS = [
  { label: '🌍 All Films',       filter: {} },
  { label: '🎬 Malayalam',       filter: { language: 'Malayalam' } },
  { label: '🎬 Tamil',           filter: { language: 'Tamil' } },
  { label: '🎬 Hindi',           filter: { language: 'Hindi' } },
  { label: '🎬 Telugu',          filter: { language: 'Telugu' } },
  { label: '🎬 Korean',          filter: { language: 'Korean' } },
  { label: '🎬 English',         filter: { language: 'English' } },
  { label: '🔫 Action',          filter: { genre: 'Action' } },
  { label: '🔍 Thriller',        filter: { genre: 'Thriller' } },
  { label: '😂 Comedy',          filter: { genre: 'Comedy' } },
  { label: '💀 Horror',          filter: { genre: 'Horror' } },
  { label: '🚀 Sci-Fi',          filter: { genre: 'Sci-Fi' } },
  { label: '❤️ Romance',         filter: { genre: 'Romance' } },
  { label: '📺 Series Only',     filter: { type: 'series' } },
  { label: '🎥 Movies Only',     filter: { type: 'movie' } },
];

export default function RankingsPage({ onAuthOpen, onAdminClick, onChatClick, onBattleClick, onRankingsClick, onTasteClick, onFilmClick }) {
  const [preset,   setPreset]   = useState(0);
  const [films,    setFilms]    = useState([]);
  const [loading,  setLoading]  = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const data = await getRankedFilms(PRESETS[preset].filter);
    setFilms(data);
    setLoading(false);
  }, [preset]);

  useEffect(() => { load(); }, [load]);

  const medals = ['🥇', '🥈', '🥉'];

  return (
    <div style={{ minHeight: '100vh', background: 'var(--black)', display: 'flex', flexDirection: 'column' }}>
      <Navbar onAuthOpen={onAuthOpen} onAdminClick={onAdminClick} onChatClick={onChatClick} onBattleClick={onBattleClick} onRankingsClick={onRankingsClick} onTasteClick={onTasteClick} />

      <div style={{ flex: 1, maxWidth: 860, width: '100%', margin: '0 auto', padding: '32px 16px' }}>

        {/* Header */}
        <div style={{ marginBottom: 28 }}>
          <h1 style={{ fontFamily: "'Playfair Display', serif", fontSize: 'clamp(24px,5vw,38px)', fontWeight: 900, color: 'var(--text)', marginBottom: 6 }}>
            🏆 Community Rankings
          </h1>
          <p style={{ color: 'var(--text-muted)', fontSize: 15 }}>
            Ranked by community ratings. Every vote counts.
          </p>
        </div>

        {/* Preset chips */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 28 }}>
          {PRESETS.map((p, i) => (
            <button key={i} onClick={() => setPreset(i)} style={{
              padding: '7px 14px', borderRadius: 20, border: 'none', cursor: 'pointer',
              fontFamily: "'Crimson Text', serif", fontSize: 14, transition: 'all 0.15s',
              background: preset === i ? 'var(--gold)' : 'var(--surface2)',
              color: preset === i ? 'var(--black)' : 'var(--text-dim)',
              fontWeight: preset === i ? 700 : 400,
              boxShadow: preset === i ? '0 2px 12px rgba(212,175,55,0.3)' : 'none',
            }}>{p.label}</button>
          ))}
        </div>

        {/* Loading */}
        {loading && (
          <div style={{ textAlign: 'center', padding: 80 }}>
            <div style={{ width: 40, height: 40, border: '3px solid var(--border)', borderTopColor: 'var(--gold)', borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto' }} />
          </div>
        )}

        {/* Empty */}
        {!loading && films.length === 0 && (
          <div style={{ textAlign: 'center', padding: '60px 20px', background: 'var(--surface)', borderRadius: 16, border: '1px solid var(--border)' }}>
            <div style={{ fontSize: 48, marginBottom: 12 }}>🎭</div>
            <div style={{ color: 'var(--text-dim)', fontSize: 16 }}>No rated films in this category yet.</div>
            <div style={{ color: 'var(--text-muted)', fontSize: 14, marginTop: 6 }}>Be the first to rate a film!</div>
          </div>
        )}

        {/* Rankings list */}
        {!loading && films.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {films.map((film, idx) => {
              const hasPoster = film.poster_url && film.poster_url.startsWith('http');
              return (
                <div key={film.id} onClick={() => onFilmClick && onFilmClick(film)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 16,
                    background: idx < 3 ? `rgba(212,175,55,${0.06 - idx * 0.015})` : 'var(--surface)',
                    border: `1px solid ${idx < 3 ? 'rgba(212,175,55,0.2)' : 'var(--border)'}`,
                    borderRadius: 12, padding: '12px 18px', cursor: 'pointer',
                    transition: 'all 0.18s',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--gold-dim)'; e.currentTarget.style.transform = 'translateX(4px)'; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = idx < 3 ? 'rgba(212,175,55,0.2)' : 'var(--border)'; e.currentTarget.style.transform = 'translateX(0)'; }}
                >
                  {/* Rank */}
                  <div style={{ width: 36, flexShrink: 0, textAlign: 'center' }}>
                    {idx < 3 ? (
                      <span style={{ fontSize: 24 }}>{medals[idx]}</span>
                    ) : (
                      <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 15, color: 'var(--text-muted)', fontWeight: 600 }}>
                        #{idx + 1}
                      </span>
                    )}
                  </div>

                  {/* Poster thumbnail */}
                  <div style={{ width: 44, height: 60, borderRadius: 6, overflow: 'hidden', background: 'var(--surface2)', flexShrink: 0 }}>
                    {hasPoster ? (
                      <img src={film.poster_url} alt={film.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    ) : (
                      <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22 }}>
                        {film.poster_emoji || '🎬'}
                      </div>
                    )}
                  </div>

                  {/* Info */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 16, fontWeight: 700, color: 'var(--text)', marginBottom: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {film.title}
                    </div>
                    <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>
                      {film.language}{film.year ? ` · ${film.year}` : ''}
                      {film.type === 'series' && <span style={{ marginLeft: 6, color: '#60a5fa', fontSize: 11 }}>📺</span>}
                    </div>
                  </div>

                  {/* Rating */}
                  <div style={{ textAlign: 'right', flexShrink: 0 }}>
                    <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 20, fontWeight: 700, color: 'var(--gold)' }}>
                      ★ {film.avgRating.toFixed(1)}
                    </div>
                    <div style={{ color: 'var(--text-muted)', fontSize: 12 }}>
                      {film.ratingCount} vote{film.ratingCount !== 1 ? 's' : ''}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {!loading && films.length > 0 && (
          <div style={{ textAlign: 'center', marginTop: 20, color: 'var(--text-muted)', fontSize: 13 }}>
            Showing {films.length} ranked film{films.length !== 1 ? 's' : ''} · Rate more films to improve the list
          </div>
        )}
      </div>
    </div>
  );
}
